import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { BiSolidEditAlt } from "react-icons/bi";
import { MdClose, MdDelete } from "react-icons/md";
import { modal } from "../../../util/useModal";
import { getCalendarStatusClass } from "../../../components/AppCalendar/calendarUtils";
import { formatEventDate } from "../../../util/eventDateTime";

const LIST_STATUS_FILTERS = [
  { id: "all", label: "all" },
  { id: "active", label: "active" },
  { id: "awaiting-review", label: "pending" },
  { id: "rejected", label: "rejected" },
];

const getPostedStatusPillClass = (status) => {
  const normalized = getCalendarStatusClass(status);

  if (normalized === "rejected") {
    return "border-danger-line bg-danger-soft text-danger";
  }

  if (normalized === "awaiting-review") {
    return "border-warning-line bg-warning-soft text-warning";
  }

  return "border-success-line bg-success-soft text-success";
};

const getPostedStatusLabel = (status) => {
  const normalized = getCalendarStatusClass(status);
  if (normalized === "awaiting-review") return "pending";
  return normalized?.replaceAll("-", " ") || "not set";
};

const formatListDate = (date, options) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, options).toLowerCase();
};

const getListEventDate = (event) =>
  formatEventDate(
    event?.date?.start || event?.nextOccurence?.start,
    event,
    "MMM d, yyyy"
  ) || null;

const getUploadedDate = (event) =>
  formatListDate(event?.createdAt, {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

// status filters: outlined in their color, filled solid with white text when selected
const STATUS_FILTER_SELECTED_CLASS = {
  active: "border-success-strong bg-success-strong text-on-fill",
  "awaiting-review": "border-warning-strong bg-warning-strong text-on-fill",
  rejected: "border-danger-strong bg-danger-strong text-on-fill",
};

const getListFilterClass = (filterId, selected) => {
  if (STATUS_FILTER_SELECTED_CLASS[filterId]) {
    if (selected) return STATUS_FILTER_SELECTED_CLASS[filterId];
    if (filterId === "awaiting-review") {
      return "posted-events-pending-filter border-warning text-warning";
    }
    return getPostedStatusPillClass(filterId);
  }

  return selected
    ? "border-[var(--inyc-secondary)] bg-[var(--inyc-secondary)] text-[var(--inyc-secondary-text-color)]"
    : "border-transparent bg-[var(--inyc-input-background-color)] text-[var(--inyc-primary-text-color-light)] hover:text-[var(--inyc-primary-text-color)]";
};

const PostedEventsListDrawer = ({
  isOpen,
  onClose,
  events = [],
  filteredEvents = [],
  enabledStatuses,
  defaultStatusFilters,
  onStatusFilterClick,
  onEditEvent,
  onCancelEvent,
  onOpenEvent,
  // inline: render in the page instead of a slide-in panel (bookings "list" view); no close button
  inline = false,
  // wording (bookings passes its own)
  title = "posted list",
  countNoun = "submissions",
  emptyLabel = "no posted events yet",
  // bookings list shows just the title
  showCount = true,
}) => {
  useEffect(() => {
    if (!isOpen || inline) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, inline, onClose]);

  const allStatusesSelected =
    enabledStatuses.length === defaultStatusFilters.length &&
    defaultStatusFilters.every((status) => enabledStatuses.includes(status));

  const content = (
    <>
      <div className="border-b-[1px] border-[var(--inyc-input-border-color)] px-kit-16 py-kit-16 md:px-kit-20">
        <div className="flex items-start justify-between gap-kit-16">
          <div className="min-w-0 flex-1">
            <div className="font-['Primary-Bold'] text-kit-md lowercase leading-none text-[var(--inyc-primary-text-color)]">
              {title}
            </div>
            {showCount && (
              <div className="mt-kit-4 font-['Primary-Bold'] text-kit-md lowercase leading-none text-[var(--inyc-primary-text-color)]">
                {events.length || 0} {countNoun}
              </div>
            )}
          </div>
          {!inline && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-[2.15rem] w-[2.15rem] shrink-0 items-center justify-center rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] text-[var(--inyc-primary-text-color-light)] transition-colors duration-200 hover:border-[var(--inyc-secondary)] hover:text-[var(--inyc-primary-text-color)]"
              aria-label="close posted events list"
            >
              <MdClose />
            </button>
          )}
        </div>

        <div className="mt-kit-16 flex flex-wrap items-center gap-kit-8">
          {LIST_STATUS_FILTERS.map((filter) => {
            const selected =
              filter.id === "all"
                ? allStatusesSelected
                : enabledStatuses.length === 1 && enabledStatuses[0] === filter.id;

            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => onStatusFilterClick(filter.id)}
                className={`h-[22px] w-[64px] rounded-kit-full border-[1px] px-0 py-0 text-kit-2xs font-['Primary-Bold'] lowercase leading-none transition-colors duration-200 ${getListFilterClass(filter.id, selected)}`}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-kit-16 py-kit-16 md:px-kit-20">
        <div className="flex flex-col gap-kit-12">
          {filteredEvents.map((event) => {
            const statusLabel = getPostedStatusLabel(event.status);
            const eventDate = getListEventDate(event);
            const uploadedDate = getUploadedDate(event);

            return (
              <article
                key={event._id}
                className="posted-event-card group rounded-kit-lg border-[1px] bg-[var(--inyc-primary)] px-kit-16 py-kit-12 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-kit-16">
                  <div className="min-w-0">
                    <div className="line-clamp-2 font-['Primary-Bold'] text-kit-base leading-[1.18] text-[var(--inyc-primary-text-color)]">
                      {event.name}
                    </div>
                    <div className="mt-kit-6 text-kit-2xs leading-[1.2] text-[var(--inyc-primary-text-color-light)]">
                      {eventDate && <span>{eventDate}</span>}
                      {eventDate && uploadedDate && <span> · </span>}
                      {uploadedDate && <span>uploaded {uploadedDate}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-kit-8">
                    <span className={`inline-flex w-[64px] justify-center rounded-kit-full border-[1px] px-0 py-kit-4 text-kit-2xs font-['Primary-Bold'] leading-none ${getPostedStatusPillClass(event.status)}`}>
                      {statusLabel}
                    </span>
                    <button
                      type="button"
                      onClick={() => onEditEvent(event)}
                      className="posted-event-edit-btn inline-flex h-[22px] w-[64px] items-center justify-center gap-kit-4 rounded-kit-full border-[1px] px-0 py-0 text-kit-2xs font-['Primary-Bold'] leading-none transition-colors duration-200"
                    >
                      <BiSolidEditAlt />
                      edit
                    </button>
                  </div>
                </div>

                {event.status === "rejected" && event.rejection_message && (
                  <div className="mt-kit-10">
                    <button
                      type="button"
                      onClick={() => {
                        modal(() => (
                          <div className="max-w-[min(88vw,420px)]">
                            <div className="mb-kit-12 font-['Primary-Bold'] text-kit-md text-[var(--inyc-primary-text-color)]">
                              rejection note
                            </div>
                            <div className="whitespace-pre-wrap rounded-kit-md border-[1px] border-danger-line bg-danger-soft p-kit-16 text-kit-sm leading-[1.5] text-[var(--inyc-primary-text-color)]">
                              {event.rejection_message}
                            </div>
                          </div>
                        ));
                      }}
                      className="rounded-kit-full border-[1px] border-danger-line px-kit-8 py-kit-4 text-kit-2xs font-['Primary-Medium'] text-danger hover:bg-danger-soft"
                    >
                      view note
                    </button>
                  </div>
                )}

                <div className="mt-kit-8 flex items-center justify-between gap-kit-12">
                  <div className="flex flex-wrap gap-kit-8">
                    <button
                      type="button"
                      onClick={() => onCancelEvent(event)}
                      className="inline-flex h-[22px] w-[64px] items-center justify-center gap-kit-4 rounded-kit-full bg-danger-strong px-0 py-0 text-kit-2xs font-['Primary-Bold'] leading-none text-on-fill transition-colors duration-200 hover:bg-danger-strong"
                    >
                      <MdDelete />
                      delete
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onOpenEvent(event)}
                    className="inline-flex h-[22px] w-[64px] items-center justify-center rounded-kit-full bg-[var(--inyc-secondary)] text-kit-2xs font-['Primary-Bold'] leading-none text-[var(--inyc-secondary-text-color)]"
                  >
                    open
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {events.length === 0 ? (
          <div className="mt-kit-12 rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] p-kit-20 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
            {emptyLabel}
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="mt-kit-12 rounded-kit-lg border-[1px] border-dashed border-[var(--inyc-input-border-color)] p-kit-20 text-center text-kit-sm text-[var(--inyc-primary-text-color-light)]">
            no events match these filters
          </div>
        ) : null}
      </div>
    </>
  );

  if (inline) {
    return (
      <section className="rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)]">
        {content}
      </section>
    );
  }

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[9996] transition-opacity duration-300 ${
          isOpen
            ? "pointer-events-auto bg-black/28 opacity-100 md:bg-black/10"
            : "pointer-events-none bg-black/0 opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        aria-hidden={!isOpen}
        className={`fixed inset-x-0 bottom-[var(--inyc-bottom-navbar-height)] top-0 z-[9997] flex transform-gpu flex-col border-l-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] shadow-[-22px_0_70px_rgba(0,0,0,0.22)] transition-transform duration-300 ease-out md:bottom-[1rem] md:left-auto md:right-[1rem] md:top-[4.5rem] md:w-[min(520px,calc(100vw-2rem))] md:rounded-kit-xl md:border-[1px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {content}
      </aside>
    </>,
    document.body
  );
};

export default PostedEventsListDrawer;
